import {
  Breadcrumbs,
  Link as MuiLink,
  Typography,
  Button,
  Box,
  Grid,
  Paper,
} from "@mui/material"
import { Link, useParams, useNavigate } from "react-router-dom"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import { useQueryClient } from "@tanstack/react-query"
import { apiFetchWeb } from "@/hooks/apiFetchWeb"
import { toast } from "sonner"
import { CrudProvider } from "@/context/crudContext"
import { DetailTable } from "@/components/Tables/DetailTable"
import { getColumns } from "@/pages/products/DetailColumns"
import OrderSummary from "@/components/Orders/OrderSummary"
import CustomerDetails from "@/components/Products/CustomerDetails"
import { isWeightable } from "@/lib/utils"
import { formatToDevanagariStyle } from "@/utilities/helper"
import { useState } from "react"

const WebsiteOrderDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isAccepting, setIsAccepting] = useState(false)
  const { data: response, isLoading } = useCustomQuery(
    ["order-detail", id],
    `order/${id}`
  )

  if (isLoading) return <Typography>Loading...</Typography>

  const order = response?.data

  const handleAcceptOrder = async () => {
    setIsAccepting(true)
    try {
      await apiFetchWeb(`order/${id}/confirm`, {
        method: "POST",
      })
      toast.success("Order confirmed successfully")
      await queryClient.invalidateQueries({
        queryKey: ["order-detail", id],
      })
      await queryClient.invalidateQueries({
        queryKey: ["orders/pending-website"],
      })
    } catch (error) {
      toast.error(error?.message || "Failed to confirm order")
    } finally {
      setIsAccepting(false)
    }
  }

  const isPending = order?.status === 2 || order?.status_parsed === "Pending"

  const returnItemColumn = [
    {
      header: "SKU",
      accessorKey: "sku",
      cell: ({ row }) => ` ${row.original.product.sku} %`,
    },
    {
      header: "CODE",
      accessorKey: "code",
      cell: ({ row }) => ` ${row.original.product?.code}`,
    },
    {
      header: "QUANTITY",
      accessorKey: "quantity",
    },
    {
      header: "PRICE",
      accessorKey: "total_price",
      cell: ({ row }) =>
        `Rs. ${formatToDevanagariStyle(row.original.total_price)}`,
    },
  ]

  return (
    <Box>
      {/* Breadcrumbs */}
      <Box
        sx={{
          marginBottom: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Breadcrumbs aria-label="breadcrumb">
          <MuiLink component={Link} to="/" underline="hover" color="inherit">
            Dashboard
          </MuiLink>
          <MuiLink
            component={Link}
            to="/website-orders"
            underline="hover"
            color="inherit"
          >
            Website Orders
          </MuiLink>
          <Typography color="text.primary">{order.code}</Typography>
        </Breadcrumbs>
        {isPending && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleAcceptOrder}
            disabled={isAccepting}
            sx={{ ml: 2 }}
          >
            {isAccepting ? "Accepting..." : "Accept Order"}
          </Button>
        )}
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Product List Section */}
        <Grid item xs={12} md={8}>
          <Paper elevation={1} sx={{ paddingX: 3, paddingTop: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} marginBottom={2}>
              Products
            </Typography>
            <CrudProvider>
              <ProductList order={order} isLoading={isLoading} />
            </CrudProvider>
          </Paper>
          {order.sales_return && (
            <Paper elevation={1} sx={{ paddingX: 3, paddingTop: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} marginBottom={2}>
                Return Items
              </Typography>
              <DetailTable
                label="Return Items"
                columns={returnItemColumn}
                data={order.sales_return?.items || []}
                isLoading={false}
              />
            </Paper>
          )}
        </Grid>

        {/* Sidebar: Customer + Summary */}
        <Grid item xs={12} md={4}>
          <Box display="flex" flexDirection="column" gap={3}>
            <CustomerDetails order={order} />
            {order.split_payments.length > 0 && (
              <div>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  marginBottom={2}
                >
                  Split Payments
                </Typography>
                {order.split_payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex justify-between items-center"
                  >
                    <Typography variant="body1" fontWeight={600}>
                      {payment.payment_method_name}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      Rs. {formatToDevanagariStyle(payment.amount)}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
            <OrderSummary order={order} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

function ProductList({ order, isLoading }) {
  const columns = getColumns(isWeightable)
  return (
    <DetailTable
      label="Website Orders"
      columns={columns}
      data={order.order_items}
      isLoading={isLoading}
    />
  )
}

export default WebsiteOrderDetails
